import { Component, inject, LOCALE_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AmplifyAuthenticatorModule } from '@aws-amplify/ui-angular';
import { signUp, type SignUpInput } from 'aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import { AuthService } from '../auth.service';

@Component({
	selector: 'app-login',
	imports: [AmplifyAuthenticatorModule, FormsModule],
	templateUrl: './login.component.html',
	styleUrl: './login.component.scss'
})
export class LoginComponent {
	private auth = inject(AuthService);
	private locale = inject(LOCALE_ID);
	loggedIn = this.auth.loggedIn;

	optInProdUpdates = false;
	acceptedTerms = false;
	termsUrl = $localize`:@@TermsUrl:https://www.ratio-electric.com/en/terms-conditions`;

	formFields = {
		signIn: {
			username: {
				label: $localize`:@@EmailLabel:Email address`,
				placeholder: $localize`:@@EmailPlaceholder:Enter your email address`,
			},
			password: {
				label: $localize`:@@PasswordLabel:Password`,
				placeholder: $localize`:@@PasswordPlaceholder:Enter your password`,
			},
		},
		forgotPassword: {
			username: {
				label: $localize`:@@EmailLabel:Email address`,
				placeholder: $localize`:@@EmailPlaceholder:Enter your email address`,
			},
		},
		signUp: {
			name: {
				label: $localize`:@@FirstNameLabel:First name`,
				placeholder: $localize`:@@FirstNamePlaceholder:Enter your first name`,
				order: 1,
			},
			family_name: {
				label: $localize`:@@LastNameLabel:Last name`,
				placeholder: $localize`:@@LastNamePlaceholder:Enter your last name`,
				order: 2,
			},
			username: {
				label: $localize`:@@EmailLabel:Email address`,
				placeholder: $localize`:@@EmailPlaceholder:Enter your email address`,
				order: 3,
			},
			password: {
				label: $localize`:@@PasswordLabel:Password`,
				placeholder: $localize`:@@PasswordPlaceholder:Enter your password`,
				order: 4,
			},
		},
	};

	services = {
		handleSignUp: (input: SignUpInput) => {
			if (!this.acceptedTerms) {
				return Promise.reject(new Error($localize`:@@TermsRequiredError:Please accept the terms and conditions to continue.`));
			}
			return signUp({
				...input,
				options: {
					...input.options,
					userAttributes: {
						...input.options?.userAttributes,
						'custom:opt_in_prod_updates': String(this.optInProdUpdates),
					},
				},
			});
		},
	};

	constructor() {
		I18n.setLanguage(this.locale);
		I18n.putVocabulariesForLanguage(this.locale, {
			'Sign In': $localize`:@@LoginSignInTab:Sign In`,
			'Create Account': $localize`:@@LoginCreateAccountTab:Create Account`,
			'Sign in': $localize`:@@LoginSignInButton:Sign in`,
			'Forgot your password?': $localize`:@@LoginForgotPassword:Forgot your password?`,
			'Confirm Password': $localize`:@@LoginConfirmPasswordLabel:Confirm Password`,
			'Please confirm your Password': $localize`:@@LoginConfirmPasswordPlaceholder:Please confirm your Password`,
			'Username should be an email.': $localize`:@@LoginUsernameEmailError:Please enter a valid email address.`,
		});
	}
}

/*

More strings from AmplifyUI (`enDict`) that can be translated:

'Account recovery requires verified contact information'
'Add your Profile' / 'Add your Website'
'Authenticator App (TOTP)'
'Back to Sign In'
'Change Password' / 'Changing'
'Code' / 'Confirmation Code' / 'Confirm Password'
'Confirm Email Code' / 'Confirm SMS Code' / 'Confirm MFA Code' / 'Confirm TOTP Code'
'Confirm Sign Up' / 'Confirm' / 'Confirming'
'Create Account' / 'Create a new account' / 'Creating Account'
'Dismiss alert'
'Email' / 'Email Message'
'Enter your Birthdate/Code/Confirmation Code/Email/Family Name/Given Name/Middle Name/Name/Nickname/Password/phone number/Preferred Username/username'
'Forgot password?' / 'Forgot your password?'
'Hide password' / 'Show password'
'It may take a minute to arrive'
'Loading'
'Multi-Factor Authentication' / 'Multi-Factor Authentication Setup'
'New password'
'or'
'Password' / 'Phone Number'
'Please confirm your Password'
'Resend Code' / 'Reset your password' / 'Reset your Password'
'Select MFA Type'
'Send code' / 'Send Code' / 'Sending'
'Setup Email' / 'Setup TOTP'
'Sign in to your account'
'Sign In with Amazon/Apple/Facebook/Google'
'Sign in' / 'Sign In' / 'Signing in'
'Skip' / 'Submit' / 'Submitting'
'Text Message (SMS)'
'Username'
'Verify Contact' / 'Verify'
'We Emailed You' / 'We Sent A Code' / 'We Texted You'
'Your code is on the way. To log in, enter the code we emailed to'
'Your code is on the way. To log in, enter the code we sent you'
'Your code is on the way. To log in, enter the code we texted to'

Extra Cognito error messages

'Username should be an email.'
'Incorrect username or password.'
'User does not exist.'
'User is not confirmed.'
'Invalid verification code provided, please try again.'
'An account with the given email already exists.'
'Password did not conform with policy: ...'

*/